import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, Cell } from 'recharts';
import type { ConversionMetrics, InventoryMetrics } from '@/lib/admin/analytics';

interface ConversionFunnelProps {
  inventory: InventoryMetrics;
  conversion: ConversionMetrics;
}

const chartConfig = {
  count: {
    label: 'Units',
    color: 'hsl(217, 91%, 60%)',
  },
};

export function ConversionFunnel({ inventory, conversion }: ConversionFunnelProps) {
  const funnelData = [
    {
      stage: 'Available',
      count: inventory.available,
      percentage: inventory.availablePercent,
      color: 'hsl(142, 76%, 36%)',
    },
    {
      stage: 'Reserved',
      count: inventory.reserved,
      percentage: inventory.reservedPercent,
      color: 'hsl(43, 96%, 56%)',
    },
    {
      stage: 'Sold',
      count: inventory.sold,
      percentage: inventory.soldPercent,
      color: 'hsl(0, 84%, 60%)',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Conversion Funnel</CardTitle>
        <CardDescription>Sales pipeline progression</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer
          config={chartConfig}
          className="h-[300px] w-full"
        >
          <BarChart
            data={funnelData}
            layout="vertical"
            margin={{ top: 20, right: 30, left: 60, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis dataKey="stage" type="category" width={50} />
            <ChartTooltip
              content={<ChartTooltipContent />}
              formatter={(value: number, name: string, props: { payload?: { percentage?: number } }) => [
                `${value} units (${props.payload?.percentage?.toFixed(1) ?? '0.0'}%)`,
                'Count',
              ]}
            />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
              {funnelData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
        <div className="mt-4 grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-sm text-muted-foreground">Available → Reserved</div>
            <div className="text-lg font-semibold">
              {conversion.availableToReserved.toFixed(1)}%
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Reserved → Sold</div>
            <div className="text-lg font-semibold">
              {conversion.reservedToSold.toFixed(1)}%
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Overall Conversion</div>
            <div className="text-lg font-semibold">
              {conversion.overallConversion.toFixed(1)}%
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

